/*!
=========================================================
* Muse Ant Design Dashboard - v1.0.0
=========================================================
*/
import { useState, useEffect } from "react";
import { Row, Col, Card, message, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import CreateUserModal from "../components/team-management/CreateUserModal";
// APIs
import {
    get_team_memebersApi,
    updateUserApi,
    deleteUserApi,
    register_team_memberApi
} from "../api/admin.apis";
import { meApi } from "../api/auth.api";

// Components (reuse same UI components)
import {
    UserFilters,
    UserModalView,
    UserModalEdit
} from "../components/users-management";
import { TeamTable } from "../components/team-management";
const { confirm } = Modal;

function TeamManagement() {
    const [teamData, setTeamData] = useState([]);
    const [filteredTeam, setFilteredTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    // modal states
    const [viewModalVisible, setViewModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    // loading states
    const [editLoading, setEditLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    useEffect(() => {
        fetchTeamMembers();
        fetchMe();
    }, []);

    /* =========================
       FETCH CURRENT USER
    ========================== */
    const fetchMe = async () => {
        try {
            const response = await meApi();
            if (response.data.status === "success") {
                setCurrentUser(response.data.data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    /* =========================
       FETCH TEAM MEMBERS
    ========================== */
    const handleCreateUser = async (values) => {
        try {
            setCreateLoading(true);

            const response = await register_team_memberApi(values);

            if (response.data.status === "success") {
                message.success("User created successfully");
                setCreateModalVisible(false);
                fetchTeamMembers();
            }
        } catch (err) {
            message.error("Failed to create user");
        } finally {
            setCreateLoading(false);
        }
    };

    const fetchTeamMembers = async () => {
        try {
            setLoading(true);
            const response = await get_team_memebersApi();

            if (response.data.status === "success") {
                const members = response.data.data.map(m => ({
                    ...m,
                    key: m._id
                }));
                setTeamData(members);
                filterMembers(searchText, statusFilter, members);
            } else {
                message.error("Failed to load team members");
            }
        } catch (error) {
            console.error(error);
            message.error("Error loading team members");
        } finally {
            setLoading(false);
        }
    };

    /* =========================
       FILTER LOGIC
    ========================== */
    const filterMembers = (search, filter, members = teamData) => {
        let data = members;

        if (search) {
            const q = search.toLowerCase();
            data = data.filter(m =>
                m.full_name?.toLowerCase().includes(q) ||
                m.email?.toLowerCase().includes(q) ||
                m.role?.toLowerCase().includes(q)
            );
        }

        if (filter === "active") data = data.filter(m => m.is_active);
        if (filter === "inactive") data = data.filter(m => !m.is_active);

        setFilteredTeam(data);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        filterMembers(value, statusFilter);
    };

    const handleFilterChange = (e) => {
        const value = e.target.value;
        setStatusFilter(value);
        filterMembers(searchText, value);
    };

    /* =========================
       ACTIONS
    ========================== */
    const handleView = (member) => {
        setSelectedMember(member);
        setViewModalVisible(true);
    };

    const handleEdit = (member) => {
        setSelectedMember(member);
        setEditModalVisible(true);
    };

    const handleToggleStatus = async (member) => {
        try {
            const response = await updateUserApi(member._id, {
                is_active: !member.is_active
            });

            if (response.data.status === "success") {
                message.success("Status updated successfully");
                fetchTeamMembers();
            }
        } catch {
            message.error("Failed to update status");
        }
    };

    const handleDelete = (member) => {
        confirm({
            title: "Delete team member?",
            icon: <ExclamationCircleOutlined />,
            content: `This will permanently delete ${member.full_name}.`,
            okType: "danger",
            async onOk() {
                await deleteMember(member._id);
            }
        });
    };

    const deleteMember = async (id) => {
        try {
            setDeleteLoading(true);
            const response = await deleteUserApi(id);
            if (response.data.status === "success") {
                message.success("Team member deleted");
                const updated = teamData.filter(m => m._id !== id);
                setTeamData(updated);
                filterMembers(searchText, statusFilter, updated);
            }
        } catch {
            message.error("Failed to delete member");
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleEditSubmit = async (values) => {
        try {
            setEditLoading(true);
            const response = await updateUserApi(selectedMember._id, values);
            if (response.data.status === "success") {
                message.success("Team member updated");
                setEditModalVisible(false);
                fetchTeamMembers();
            }
        } catch {
            message.error("Failed to update member");
        } finally {
            setEditLoading(false);
        }
    };
    return (
        <div className="tabled">
            <Row gutter={[24, 0]}>
                <Col xs="24" xl={24}>
                    <Card
                        bordered={false}
                        className="criclebox tablespace mb-24"
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span>Team Management ({filteredTeam.length} members)</span>
                                {/* <UserFilters
                                    onSearch={handleSearch}
                                    onFilterChange={handleFilterChange}
                                    onRefresh={fetchTeamMembers}
                                    loading={loading}
                                /> */}
                                {currentUser?.user?.role === "super_admin" && (
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setCreateModalVisible(true)}
                                    >
                                        Create User
                                    </Button>
                                )}

                            </div>
                        }
                    >
                        <TeamTable
                            currentUser={currentUser}
                            members={filteredTeam}
                            loading={loading}
                            onView={handleView}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onToggleStatus={handleToggleStatus}
                        />
                    </Card>
                </Col>
            </Row>

            {/* View Modal */}
            <UserModalView
                visible={viewModalVisible}
                user={selectedMember}
                onClose={() => setViewModalVisible(false)}
                onEdit={() => {
                    setViewModalVisible(false);
                    handleEdit(selectedMember);
                }}
            />

            {/* Edit Modal */}
            <UserModalEdit
                visible={editModalVisible}
                user={selectedMember}
                loading={editLoading}
                onSubmit={handleEditSubmit}
                onClose={() => setEditModalVisible(false)}
            />
            <CreateUserModal
                visible={createModalVisible}
                loading={createLoading}
                onSubmit={handleCreateUser}
                onClose={() => setCreateModalVisible(false)}
            />

        </div>
    );
}

export default TeamManagement;
